import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { getPublicShareDownloadUrl } from "@/shared/api";
import { Loader } from "@/shared/ui";
import { toast } from "@/shared/ui/Toast";
import { openExternalUrl } from "@/shared/lib/miniAppOpenLink";
import { sanitizeFileName, triggerWebDownload } from "@/shared/lib/downloadAudio";
import styles from "./ShareAudioBar.module.scss";
/** Таймаут запроса за presigned-ссылкой (инстанс `api` держит общие 85 с). */
const DOWNLOAD_URL_TIMEOUT_MS = 15000;
function fmt(sec) {
    if (!Number.isFinite(sec) || sec < 0)
        return "0:00";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
}
/** Компактный плеер трека на публичной странице-подарке. */
export const ShareAudioBar = ({ audio, autoPlay, token }) => {
    const ref = React.useRef(null);
    const [playing, setPlaying] = React.useState(false);
    const [current, setCurrent] = React.useState(0);
    const [duration, setDuration] = React.useState(0);
    const [downloading, setDownloading] = React.useState(false);
    // Гард от двойного тапа: стейт в замыкании обработчика «устаревает», поэтому
    // защищаемся ref-ом (нативный disabled оставляем — он для UI и a11y).
    const downloadingRef = React.useRef(false);
    // Пользователь может открыть подарок/уйти со страницы, пока идёт запрос —
    // setState после размонтирования не нужен (тот же приём, что в SharePage).
    const mountedRef = React.useRef(true);
    React.useEffect(() => () => {
        mountedRef.current = false;
    }, []);
    React.useEffect(() => {
        if (!autoPlay || !ref.current)
            return;
        // Запуск по пользовательскому жесту «Открыть» — autoplay разрешён.
        ref.current.play().then(() => setPlaying(true), () => setPlaying(false));
    }, [autoPlay]);
    const toggle = () => {
        const el = ref.current;
        if (!el)
            return;
        if (el.paused) {
            void el.play().then(() => setPlaying(true), () => setPlaying(false));
        }
        else {
            el.pause();
            setPlaying(false);
        }
    };
    const seek = (e) => {
        const el = ref.current;
        if (!el)
            return;
        const t = Number(e.target.value);
        el.currentTime = t;
        setCurrent(t);
    };
    /**
     * Скачивание по ПРЯМОЙ (presigned) ссылке из хранилища: ручка by-token отдаёт
     * `{ url }` с `Content-Disposition: attachment`. Фолбэк — прямой url трека
     * (как в SongHub). В мини-аппе TG/MAX ссылку отдаём мосту: в вебвью
     * `<a download>` навигирует/проигрывает вместо сохранения.
     *
     * Имя файла берём из `audio.title` КАК ЕСТЬ: если шара указывает на минус,
     * это уже строка-стем, у которой в title суффикс «(минус)» проставлен бэком
     * (SongInstrumentalService._stem_title) — дописывать свой значит получить
     * «Песня (минус) (минус).mp3».
     */
    const download = async () => {
        if (!token || downloadingRef.current)
            return;
        downloadingRef.current = true;
        setDownloading(true);
        try {
            let href = "";
            try {
                // Укороченный таймаут вместо общих 85 с инстанса api: под кнопкой
                // крутится спиннер, и молчать полторы минуты нельзя.
                href = (await getPublicShareDownloadUrl(token, DOWNLOAD_URL_TIMEOUT_MS)) || "";
            }
            catch (e) {
                console.error("ShareAudioBar: download-url failed, fallback to audio url", e);
            }
            // Ручка не ответила ИЛИ вернула пустой url — фолбэк на прямой url трека.
            if (!href)
                href = audio.url || "";
            if (!href) {
                toast.error("Не удалось скачать песню");
                return;
            }
            if (openExternalUrl(href))
                return;
            const fileName = `${sanitizeFileName(audio.title || "song")}.mp3`;
            await triggerWebDownload(href, fileName);
        }
        catch (e) {
            // Любой провал скачивания (не только пустой href) должен быть виден
            // пользователю — иначе спиннер просто гаснет без объяснений.
            console.error("ShareAudioBar: download failed", e);
            toast.error("Не удалось скачать песню");
        }
        finally {
            downloadingRef.current = false;
            if (mountedRef.current)
                setDownloading(false);
        }
    };
    const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : 0;
    const safeCurrent = Math.min(Math.max(0, current), safeDuration || 0);
    return (_jsxs("div", { className: styles.audioBar, children: [_jsx("audio", { ref: ref, src: audio.url, preload: "metadata", onTimeUpdate: (e) => setCurrent(e.currentTarget.currentTime), onLoadedMetadata: (e) => {
                    const d = e.currentTarget.duration;
                    setDuration(Number.isFinite(d) ? d : 0);
                }, onEnded: () => {
                    setPlaying(false);
                    setCurrent(0);
                } }), audio.cover_url && (_jsx("img", { className: styles.cover, src: audio.cover_url, alt: "" })), _jsx("button", { type: "button", className: styles.playBtn, onClick: toggle, "aria-label": playing ? "Пауза" : "Играть", children: playing ? (_jsxs("svg", { width: "18", height: "18", viewBox: "0 0 18 18", fill: "currentColor", "aria-hidden": "true", children: [_jsx("rect", { x: "3", y: "2", width: "4", height: "14", rx: "1" }), _jsx("rect", { x: "11", y: "2", width: "4", height: "14", rx: "1" })] })) : (_jsx("svg", { width: "18", height: "18", viewBox: "0 0 18 18", fill: "currentColor", "aria-hidden": "true", children: _jsx("path", { d: "M4 2.5v13l11-6.5z" }) })) }), _jsxs("div", { className: styles.audioMain, children: [_jsx("span", { className: styles.audioTitle, children: audio.title || "Песня-подарок" }), _jsxs("div", { className: styles.seekRow, children: [_jsx("span", { className: styles.time, children: fmt(safeCurrent) }), _jsx("input", { type: "range", min: 0, max: safeDuration || 1, step: 0.1, value: safeCurrent, onChange: seek, className: styles.seek, "aria-label": "\u041F\u0435\u0440\u0435\u043C\u043E\u0442\u043A\u0430", disabled: !safeDuration }), _jsx("span", { className: styles.time, children: fmt(safeDuration) })] })] }), token && (_jsx("button", { type: "button", className: styles.downloadBtn, onClick: () => void download(), disabled: downloading, "aria-busy": downloading, "aria-label": "\u0421\u043A\u0430\u0447\u0430\u0442\u044C \u043F\u0435\u0441\u043D\u044E", title: "\u0421\u043A\u0430\u0447\u0430\u0442\u044C \u043F\u0435\u0441\u043D\u044E", children: downloading ? (_jsx(Loader, { size: "sm", style: { color: "#fff" } })) : (_jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("path", { d: "M12 3v12" }), _jsx("path", { d: "M7 11l5 5 5-5" }), _jsx("path", { d: "M4 20h16" })] })) }))] }));
};
